import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowLeft,
    faFileAlt,
    faDownload,
    faCalendarAlt,
    faSearch,
    faArrowUp,
    faArrowDown,
    faEye,
    faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import './AccountStatementPage.css';

// Mock data for demonstration
const mockTransactions = [
    {
        id: 1,
        date: '2025-01-24',
        time: '14:35:21',
        type: 'income',
        category: '기부금',
        description: '김철수님 기부금 (응급실 CT 장비)',
        donor: '김철수',
        amount: 100000,
        balance: 15600000,
        receipt: true,
        campaign: '응급실 CT 장비 도입'
    },
    {
        id: 2,
        date: '2025-01-24',
        time: '10:20:15',
        type: 'expense',
        category: '장비구매',
        description: '의료장비 구매 - 인공호흡기',
        recipient: '메디칼시스템즈(주)',
        amount: -3500000,
        balance: 15500000,
        receipt: true,
        campaign: '중환자실 장비 지원'
    },
    {
        id: 3,
        date: '2025-01-23',
        time: '16:45:33',
        type: 'income',
        category: '기부금',
        description: '이영희님 정기후원 (월)',
        donor: '이영희',
        amount: 50000,
        balance: 19000000,
        receipt: true,
        campaign: '의료진 교육 프로그램'
    },
    {
        id: 4,
        date: '2025-01-23',
        time: '09:15:00',
        type: 'expense',
        category: '교육비',
        description: '의료진 외상 교육 프로그램 참가비',
        recipient: '대한외상학회',
        amount: -1200000,
        balance: 18950000,
        receipt: true,
        campaign: '의료진 교육 프로그램'
    },
    {
        id: 5,
        date: '2025-01-22',
        time: '15:30:45',
        type: 'income',
        category: '기부금',
        description: '박민수님 기부금 (응급처치 키트)',
        donor: '박민수',
        amount: 30000,
        balance: 20150000,
        receipt: true,
        campaign: '응급처치 키트 지원'
    },
    {
        id: 6,
        date: '2025-01-22',
        time: '11:20:30',
        type: 'expense',
        category: '물품구매',
        description: '응급처치 키트 100세트 구매',
        recipient: '메디컬서플라이(주)',
        amount: -850000,
        balance: 20120000,
        receipt: true,
        campaign: '응급처치 키트 지원'
    },
    {
        id: 7,
        date: '2025-01-21',
        time: '14:10:22',
        type: 'income',
        category: '기부금',
        description: '정상호님 기부금 (의료진 지원)',
        donor: '정상호',
        amount: 200000,
        balance: 20970000,
        receipt: true,
        campaign: '의료진 처우개선'
    },
    {
        id: 8,
        date: '2025-01-21',
        time: '08:45:00',
        type: 'income',
        category: '기부금',
        description: '최지현님 정기후원 (월)',
        donor: '최지현',
        amount: 100000,
        balance: 20770000,
        receipt: true,
        campaign: '일반 운영 지원'
    }
];

const AccountStatementPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [dateRange, setDateRange] = useState({
        start: '',
        end: ''
    });
    const [stats, setStats] = useState({
        totalIncome: 0,
        totalExpense: 0,
        currentBalance: 0,
        transactionCount: 0
    });

    useEffect(() => {
        // Simulate API call
        setTimeout(() => {
            setTransactions(mockTransactions);
            setFilteredTransactions(mockTransactions);
            calculateStats(mockTransactions);
            setLoading(false);
        }, 500);
    }, []);

    useEffect(() => {
        filterTransactions();
    }, [searchTerm, filterType, dateRange, transactions]);

    const calculateStats = (txns) => {
        const income = txns.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
        const expense = txns.filter(t => t.type === 'expense').reduce((sum, t) => sum + Math.abs(t.amount), 0);
        const latestTransaction = txns[0];
        
        setStats({
            totalIncome: income,
            totalExpense: expense,
            currentBalance: latestTransaction ? latestTransaction.balance : 0,
            transactionCount: txns.length
        });
    };

    const filterTransactions = () => {
        let filtered = [...transactions];

        // Filter by type
        if (filterType !== 'all') {
            filtered = filtered.filter(t => t.type === filterType);
        }

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(t => 
                t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (t.donor && t.donor.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (t.recipient && t.recipient.toLowerCase().includes(searchTerm.toLowerCase())) ||
                t.campaign.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by date range
        if (dateRange.start) {
            filtered = filtered.filter(t => new Date(t.date) >= new Date(dateRange.start));
        }
        if (dateRange.end) {
            filtered = filtered.filter(t => new Date(t.date) <= new Date(dateRange.end));
        }

        setFilteredTransactions(filtered);
    };

    const formatCurrency = (amount) => {
        const isNegative = amount < 0;
        const absAmount = Math.abs(amount);
        const formatted = new Intl.NumberFormat('ko-KR').format(absAmount);
        return isNegative ? `-${formatted}원` : `${formatted}원`;
    };

    const formatDate = (date, time) => {
        const d = new Date(date);
        const months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
        return `${months[d.getMonth()]} ${d.getDate()}일 ${time}`;
    };

    const downloadStatement = () => {
        // Simulate download
        const csvContent = "data:text/csv;charset=utf-8," 
            + "날짜,시간,구분,카테고리,설명,기부자/수령인,금액,잔액,캠페인\n"
            + filteredTransactions.map(t => 
                `${t.date},${t.time},${t.type === 'income' ? '입금' : '출금'},${t.category},"${t.description}",${t.donor || t.recipient || ''},${t.amount},${t.balance},"${t.campaign}"`
            ).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `account_statement_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>거래 내역을 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div className="account-page">
            {/* Header */}
            <header className="account-header">
                <div className="header-container">
                    <Link to="/" className="back-button">
                        <FontAwesomeIcon icon={faArrowLeft} /> 홈으로
                    </Link>
                    <h1 className="page-title">계좌 내역 공개 [시연용]</h1>
                    <div className="header-spacer"></div>
                </div>
            </header>

            <main className="account-main">
                <div className="main-container">
                    {/* Trust Badge */}
                    <div className="trust-section">
                        <div className="trust-badge">
                            <FontAwesomeIcon icon={faShieldAlt} className="trust-icon" />
                            <div className="trust-content">
                                <h2>100% 투명한 기부금 관리</h2>
                                <p>현재는 데모이며, 모든 입출금 내역을 실시간으로 공개할 예정이며  기부금이 어떻게 사용되는지 투명하게 확인할경우 어덯게 진행되는지 보여주는 예시입니다.</p>
                            </div>
                        </div>
                    </div>

                    {/* Statistics Cards */}
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-header">
                                <span className="stat-label">현재 잔액</span>
                                <FontAwesomeIcon icon={faEye} className="stat-icon" />
                            </div>
                            <div className="stat-value primary">{formatCurrency(stats.currentBalance)}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-header">
                                <span className="stat-label">총 입금액</span>
                                <FontAwesomeIcon icon={faArrowDown} className="stat-icon income" />
                            </div>
                            <div className="stat-value income">{formatCurrency(stats.totalIncome)}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-header">
                                <span className="stat-label">총 출금액</span>
                                <FontAwesomeIcon icon={faArrowUp} className="stat-icon expense" />
                            </div>
                            <div className="stat-value expense">{formatCurrency(stats.totalExpense)}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-header">
                                <span className="stat-label">거래 건수</span>
                                <FontAwesomeIcon icon={faFileAlt} className="stat-icon" />
                            </div>
                            <div className="stat-value">{stats.transactionCount}건</div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="filters-section">
                        <div className="filters-row">
                            <div className="search-box">
                                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="기부자, 캠페인, 설명 검색..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="search-input"
                                />
                            </div>

                            <div className="filter-buttons">
                                <button 
                                    className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                                    onClick={() => setFilterType('all')}
                                >
                                    전체
                                </button>
                                <button 
                                    className={`filter-btn ${filterType === 'income' ? 'active' : ''}`}
                                    onClick={() => setFilterType('income')}
                                >
                                    입금
                                </button>
                                <button 
                                    className={`filter-btn ${filterType === 'expense' ? 'active' : ''}`}
                                    onClick={() => setFilterType('expense')}
                                >
                                    출금
                                </button>
                            </div>

                            <div className="date-range">
                                <input
                                    type="date"
                                    value={dateRange.start}
                                    onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                                    className="date-input"
                                />
                                <span className="date-separator">~</span>
                                <input
                                    type="date"
                                    value={dateRange.end}
                                    onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                                    className="date-input"
                                />
                            </div>

                            <button onClick={downloadStatement} className="download-btn">
                                <FontAwesomeIcon icon={faDownload} />
                                내역 다운로드
                            </button>
                        </div>
                    </div>

                    {/* Transactions Table */}
                    <div className="transactions-section">
                        <div className="transactions-table">
                            <div className="table-header">
                                <div className="th-date">날짜/시간</div>
                                <div className="th-description">내역</div>
                                <div className="th-campaign">캠페인</div>
                                <div className="th-amount">금액</div>
                                <div className="th-balance">잔액</div>
                                <div className="th-receipt">영수증</div>
                            </div>
                            
                            <div className="table-body">
                                {filteredTransactions.map((transaction) => (
                                    <div key={transaction.id} className="transaction-row">
                                        <div className="td-date">
                                            <div className="date-primary">{formatDate(transaction.date, transaction.time)}</div>
                                        </div>
                                        <div className="td-description">
                                            <div className="description-main">{transaction.description}</div>
                                            <div className="description-sub">
                                                {transaction.type === 'income' ? 
                                                    `기부자: ${transaction.donor}` : 
                                                    `수령: ${transaction.recipient}`
                                                }
                                            </div>
                                        </div>
                                        <div className="td-campaign">
                                            <span className="campaign-badge">{transaction.campaign}</span>
                                        </div>
                                        <div className={`td-amount ${transaction.type}`}>
                                            {transaction.type === 'income' && <FontAwesomeIcon icon={faArrowDown} className="amount-icon" />}
                                            {transaction.type === 'expense' && <FontAwesomeIcon icon={faArrowUp} className="amount-icon" />}
                                            <span className="amount-value">{formatCurrency(transaction.amount)}</span>
                                        </div>
                                        <div className="td-balance">
                                            {formatCurrency(transaction.balance)}
                                        </div>
                                        <div className="td-receipt">
                                            {transaction.receipt && (
                                                <button className="receipt-btn">
                                                    <FontAwesomeIcon icon={faEye} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {filteredTransactions.length === 0 && (
                            <div className="no-results">
                                <p>검색 결과가 없습니다.</p>
                            </div>
                        )}
                    </div>

                    {/* Footer Info */}
                    <div className="info-section">
                        <div className="info-card">
                            <h3>투명성 원칙</h3>
                            <ul>
                                <li>모든 거래 내역은 실시간으로 업데이트됩니다</li>
                                <li>각 거래별 영수증 및 증빙 자료를 확인하실 수 있습니다</li>
                                <li>기부금은 100% 지정된 캠페인에 사용됩니다</li>
                                <li>운영비는 별도 후원금으로 충당하여 기부금을 보호합니다</li>
                            </ul>
                        </div>
                        <div className="info-card">
                            <h3>계좌 정보</h3>
                            <div className="account-info">
                                <p><strong>은행:</strong> 국민은행</p>
                                <p><strong>계좌번호:</strong> 268801-04-262502</p>
                                <p><strong>예금주:</strong> (사)한국외상센터지원단체</p>
                                <p className="info-note">※ 본 계좌는 기부금 전용이며, 다른 용도의 연락은 받지 않습니다.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AccountStatementPage;